import React, { useEffect, useState, useRef } from 'react';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { BsSearch, BsTrash, BsPeople, BsShieldCheck, BsExclamationTriangle } from 'react-icons/bs';
import { FiRefreshCw, FiUsers, FiLink, FiX } from 'react-icons/fi';
import { FaCalendarAlt, FaExternalLinkAlt, FaSortAmountDownAlt, FaSortAmountUp, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import Loader from '../../components/Loader';
import formatDate from '../../components/formatDate';
import User_Navbar from './User_Navbar';
import MainButton from '../../components/ul/MainButton';
import getISTDate from '../../components/getISTDate';
import DeleteButton from '../../components/ul/DeleteButton';

const Referral = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);
    const [totalPage, setTotalPage] = useState(0);
    const [totalRecord, setTotalRecord] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [search, setSearch] = useState('');
    const [ids, setIds] = useState([]);
    const [isDelete, setIsDelete] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [selectState, setSelectState] = useState('today');
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [sortOrder, setSortOrder] = useState(-1);
    const modalRef = useRef(null);

    const getReferralData = async (showRefresh = false) => {
        if (showRefresh) setIsRefreshing(true);
        else setIsLoading(true);

        const response = await Calling.getReferral(page, limit, startDate, endDate, '', sortOrder, search);
        if (response.status) {
            setData(response.data);
            setTotalPage(response.totalPage);
            setTotalRecord(response.totalRecord || 0);
            setHasNextPage(response.nextPage);
        } else {
            toast.error(response.message);
        }

        setIsLoading(false);
        setIsRefreshing(false);
    }

    useEffect(() => {
        getReferralData();
    }, [page, limit, startDate, endDate, sortOrder]);

    useEffect(() => {
        if (search.trim() !== '') {
            const delayDebounce = setTimeout(() => {
                getReferralData();
            }, 500);
            return () => clearTimeout(delayDebounce);
        }
    }, [search]);

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowDetailModal(false);
                setSelectedReferral(null);
            }
        };

        if (showDetailModal) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDetailModal]);

    // Close modal on ESC key
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                setShowDetailModal(false);
                setSelectedReferral(null);
            }
        };

        if (showDetailModal) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [showDetailModal]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            default:
                start = '';
                end = '';
                setSelectState('range');
        }

        setStartDate(start);
        setEndDate(end);
        setPage(1);
    };

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
            setSelectState('range');
        } else {
            setEndDate(e.target.value);
            setSelectState('range');
        }
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(1);
    }

    const handleLimit = (e) => {
        setLimit(parseInt(e.target.value));
        setPage(1);
    }

    const handleCheck = (id) => {
        if (ids.includes(id)) {
            setIds(ids.filter(item => item !== id));
        } else {
            setIds([...ids, id]);
        }
    }

    const handleAllCheck = () => {
        if (ids.length === data.length) {
            setIds([]);
        } else {
            setIds(data.map(item => item._id));
        }
    }

    const handleDeleteClick = () => {
        if (ids.length > 0) {
            setShowDeleteModal(true);
        }
    }

    const handleDelete = async () => {
        setIsDelete(true);
        const response = await Calling.deleteReferral(ids);
        if (response.status) {
            toast.success(response.message);
            getReferralData();
            setIds([]);
        } else {
            toast.error(response.message);
        }
        setIsDelete(false);
        setShowDeleteModal(false);
    }

    const handleUserClick = (id) => {
        if (id) {
            window.open(`/manage/user/all/info/${id}`, '_blank');
        }
    }

    const handleRefresh = () => {
        getReferralData(true);
    }

    const handleRowClick = (item) => {
        setSelectedReferral(item);
        setShowDetailModal(true);
    }

    const handleReportSort = () => {
        setSelectState('sort');
        setSortOrder(sortOrder === 1 ? -1 : 1);
    };

    return (
        <div className="w-full min-h-screen text-main-font">
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                        <FiUsers size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">User Management</h1>
                        <p className="text-sm text-main-subfont">Manage and monitor all users</p>
                    </div>
                </div>
            </div>

            {/* Main Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                {/* Navigation */}
                <User_Navbar selectState={'referral'} />
                <hr className="border-main-border" />

                <div className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="p-4 bg-main-exBlue/5 rounded-xl border border-main-exBlue/20">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-main-subfont">Total Referrals</p>
                                    <p className="text-2xl font-bold text-main-font mt-1">
                                        {isLoading ? (
                                            <span className="flex items-center text-main-subfont text-base">
                                                <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Loading...
                                            </span>
                                        ) : (
                                            totalRecord.toLocaleString()
                                        )}
                                    </p>
                                </div>
                                <div className="p-3 rounded-xl bg-main-exBlue/10">
                                    <FiUsers size={24} className="text-main-exBlue" />
                                </div>
                            </div>
                        </div>

                        <div className="p-4 bg-main-red/5 rounded-xl border border-main-red/20">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-main-subfont">Current Page</p>
                                    <p className="text-2xl font-bold text-main-font mt-1">{data.length}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-main-red/10">
                                    <BsPeople size={24} className="text-main-red" />
                                </div>
                            </div>
                        </div>

                        <div className="p-4 bg-main-yellow/5 rounded-xl border border-main-yellow/20">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-main-subfont">Total Pages</p>
                                    <p className="text-2xl font-bold text-main-font mt-1">{totalPage}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-main-yellow/10">
                                    <BsShieldCheck size={24} className="text-main-yellow" />
                                </div>
                            </div>
                        </div>


                    </div>
                </div>

                {/* Filters Section */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Date Range Buttons */}
                        <div className="flex flex-wrap gap-2">
                            <MainButton onClick={() => handleDateRangeFilter('today')} label={'Today'} selectState={selectState === 'today'} />
                            <MainButton onClick={() => handleDateRangeFilter('yesterday')} label={'Yesterday'} selectState={selectState === "yesterday"} />
                            <MainButton onClick={() => handleDateRangeFilter('thisWeek')} label={'This Week'} selectState={selectState === 'thisWeek'} />
                            <MainButton onClick={() => handleDateRangeFilter('thisMonth')} label={'This Month'} selectState={selectState === 'thisMonth'} />
                            <MainButton onClick={() => handleDateRangeFilter('last6Months')} label={'Last 6 Months'} selectState={selectState === 'last6Months'} />
                            <MainButton onClick={() => handleDateRangeFilter('thisYear')} label={'This Year'} selectState={selectState === 'thisYear'} />
                        </div>

                        {/* Date Inputs */}
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                type="date"
                                value={startDate}
                                name="startDate"
                                onChange={handleFormDate}
                                className={`px-4 py-2 rounded-xl border cursor-pointer transition-all ${selectState === "range"
                                    ? 'border-main-blue text-main-blue bg-main-th'
                                    : 'border-main-border text-main-subfont bg-main-white'
                                    } focus:border-main-blue focus:outline-none`}
                            />
                            <input
                                type="date"
                                value={endDate}
                                name="endDate"
                                onChange={handleFormDate}
                                className={`px-4 py-2 rounded-xl border cursor-pointer transition-all ${selectState === "range"
                                    ? 'border-main-blue text-main-blue bg-main-th'
                                    : 'border-main-border text-main-subfont bg-main-white'
                                    } focus:border-main-blue focus:outline-none`}
                            />
                        </div>
                    </div>
                </div>

                {/* Action Buttons and Search */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 items-center">
                            <MainButton
                                onClick={handleReportSort}
                                label={
                                    <span className="flex items-center gap-2">
                                        {sortOrder === -1 ? <FaSortAmountDownAlt /> : <FaSortAmountUp />}
                                        Sort
                                    </span>
                                }
                                selectState={selectState === 'sort'}
                            />
                            <button
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                                className="p-2.5 bg-main-bg hover:bg-main-thHover rounded-xl transition-all border border-main-border"
                                title="Refresh"
                            >
                                <FiRefreshCw className={`text-main-font ${isRefreshing ? 'animate-spin' : ''}`} size={18} />
                            </button>
                            {ids.length > 0 && (
                                <button
                                    onClick={handleDeleteClick}
                                    className="px-4 py-2.5 bg-red-500 text-white rounded-xl flex items-center gap-2 hover:bg-red-600 transition-colors"
                                >
                                    <BsTrash size={16} /> Delete ({ids.length})
                                </button>
                            )}
                        </div>

                        {/* Search Input */}
                        <div className="relative w-full lg:w-80">
                            <BsSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-main-subfont" size={18} />
                            <input
                                type="search"
                                className="w-full pl-11 pr-4 py-2.5 border border-main-border rounded-xl bg-main-bg focus:bg-main-white focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue focus:outline-none transition-all"
                                value={search}
                                onChange={handleSearch}
                                placeholder="Search by Code or User ID..."
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-main-th">
                                <th className="ps-4 py-4 text-left">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                            checked={ids.length === data.length && data.length > 0}
                                            onChange={handleAllCheck}
                                        />
                                    </div>
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">#</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">User</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Referrer</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Code</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Date</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Action</th>
                            </tr>
                        </thead>

                        {isLoading ? (
                            <tbody>
                                <tr>
                                    <td colSpan={7} className="text-center py-12">
                                        <Loader />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody className="divide-y divide-main-border">
                                {data.length > 0 ? data.map((item, index) => (
                                    <tr key={item._id} className="hover:bg-main-thHover transition-colors">
                                        <td className="ps-4 py-4">
                                            <input
                                                type="checkbox"
                                                checked={ids.includes(item._id)}
                                                onChange={() => handleCheck(item._id)}
                                                className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont">{(page - 1) * limit + index + 1}</td>
                                        <td className="px-4 py-4">
                                            <div
                                                className="flex items-center gap-3 cursor-pointer group"
                                                onClick={() => handleUserClick(item?.u_info?._id)}
                                            >
                                                {item?.u_info?.u_profile ? (
                                                    <img
                                                        src={item.u_info.u_profile}
                                                        alt=""
                                                        className="w-10 h-10 rounded-full object-cover border-2 border-main-border group-hover:border-main-blue transition-colors"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-main-blue text-main-white flex items-center justify-center font-semibold text-sm">
                                                        {item?.u_info?.u_name?.charAt(0).toUpperCase() || 'U'}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium text-main-font group-hover:text-main-blue transition-colors">
                                                        {item?.u_info?.u_name || 'N/A'}
                                                    </p>
                                                    <p className="text-xs text-main-subfont truncate max-w-[120px]">
                                                        {item?.u_info?.u_email || item?.u_info?.u_device || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div
                                                className="flex items-center gap-3 cursor-pointer group"
                                                onClick={() => handleUserClick(item?.r_info?._id)}
                                            >
                                                {item?.r_info?.u_profile ? (
                                                    <img
                                                        src={item.r_info.u_profile}
                                                        alt=""
                                                        className="w-10 h-10 rounded-full object-cover border-2 border-main-border group-hover:border-main-blue transition-colors"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-main-blue text-main-white flex items-center justify-center font-semibold text-sm">
                                                        {item?.r_info?.u_name?.charAt(0).toUpperCase() || 'R'}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium text-main-font group-hover:text-green-600 transition-colors">
                                                        {item?.r_info?.u_name || 'N/A'}
                                                    </p>
                                                    <p className="text-xs text-main-subfont truncate max-w-[120px]">
                                                        {item?.r_info?.u_email || item?.r_info?.u_device || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg bg-main-blue text-main-white">
                                                {item.r_code}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont">{formatDate(item.createdAt)}</td>
                                        <td className="px-4 py-4">
                                            <button
                                                onClick={() => handleRowClick(item)}
                                                className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-main-blue text-main-white hover:bg-main-blue/90 transition-colors"
                                                title="View Details"
                                            >
                                                <FaExternalLinkAlt size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={7} className="py-12">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="p-4 bg-main-bg rounded-full">
                                                    <FiUsers className="text-4xl text-main-subfont" />
                                                </div>
                                                <p className="text-main-subfont font-medium">No Referrals Found</p>
                                                <p className="text-sm text-main-subfont">Try adjusting your search criteria</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-6 border-t border-main-border">
                    <div className="flex flex-col md:flex-row justify-center items-center relative">
                        <div className="flex gap-3 mb-4 md:mb-0">
                            <button
                                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                disabled={page === 1}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${page === 1
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                <FaArrowLeft size={12} /> Previous
                            </button>
                            <button
                                onClick={() => setPage((prev) => Math.min(prev + 1, totalPage))}
                                disabled={!hasNextPage}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${!hasNextPage
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                Next <FaArrowRight size={12} />
                            </button>
                        </div>

                        <p className="text-main-subfont md:absolute md:right-0 text-sm">
                            Page{' '}
                            <span className="px-3 py-1.5 bg-main-th border border-main-border text-main-font font-medium rounded-lg">
                                {page}
                            </span>
                            {' '}of{' '}
                            <span className="font-medium text-main-font">{totalPage}</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-main-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
                            <BsExclamationTriangle className="text-3xl text-red-500" />
                        </div>

                        <h3 className="text-xl font-bold text-main-font text-center mb-2">
                            Confirm Deletion
                        </h3>

                        <p className="text-main-subfont text-center mb-6">
                            Are you sure you want to delete <span className="font-semibold text-red-500">{ids.length}</span> referral{ids.length > 1 ? 's' : ''}? This action cannot be undone.
                        </p>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                disabled={isDelete}
                                className="flex-1 px-4 py-3 bg-main-bg border border-main-border text-main-font rounded-xl font-medium hover:bg-main-thHover transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDelete}
                                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                            >
                                {isDelete ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <BsTrash /> Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail Modal */}
            {showDetailModal && selectedReferral && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div ref={modalRef} className="bg-main-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden">
                        {/* Modal Header */}
                        <div className="bg-main-blue p-5 text-main-white relative">
                            <button
                                onClick={() => { setShowDetailModal(false); setSelectedReferral(null); }}
                                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <FiX size={16} />
                            </button>
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-white/20 rounded-xl">
                                    <FiLink size={24} />
                                </div>
                                <div>
                                    <p className="text-white/80 text-sm">Referral Details</p>
                                    <h2 className="text-xl font-bold">{selectedReferral?.r_code || 'N/A'}</h2>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-5 space-y-4 max-h-[50vh] overflow-y-auto">
                            {/* User Card */}
                            <div
                                onClick={() => handleUserClick(selectedReferral?.u_info?._id)}
                                className="bg-main-th border border-main-border rounded-xl p-4 cursor-pointer hover:border-main-blue transition-all group"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {selectedReferral?.u_info?.u_profile ? (
                                            <img
                                                src={selectedReferral.u_info.u_profile}
                                                alt=""
                                                className="w-12 h-12 rounded-full object-cover border-2 border-main-border"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-main-blue text-main-white flex items-center justify-center font-bold text-lg">
                                                {selectedReferral?.u_info?.u_name?.charAt(0).toUpperCase() || 'U'}
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-xs text-main-blue font-medium uppercase tracking-wide">User</p>
                                            <p className="font-semibold text-main-font group-hover:text-main-blue transition-colors">
                                                {selectedReferral?.u_info?.u_name || 'N/A'}
                                            </p>
                                            <p className="text-xs text-main-subfont mt-0.5">{selectedReferral?.u_info?.u_email || selectedReferral?.u_info?.u_device || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <FaExternalLinkAlt className="text-main-blue group-hover:translate-x-1 transition-transform" size={12} />
                                </div>
                            </div>

                            {/* Referrer Card */}
                            <div
                                onClick={() => handleUserClick(selectedReferral?.r_info?._id)}
                                className="bg-green-50 border border-green-200 rounded-xl p-4 cursor-pointer hover:border-main-blue transition-all group"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {selectedReferral?.r_info?.u_profile ? (
                                            <img
                                                src={selectedReferral.r_info.u_profile}
                                                alt=""
                                                className="w-12 h-12 rounded-full object-cover border-2 border-green-300"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-main-blue text-main-white flex items-center justify-center font-bold text-lg">
                                                {selectedReferral?.r_info?.u_name?.charAt(0).toUpperCase() || 'R'}
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Referrer</p>
                                            <p className="font-semibold text-main-font group-hover:text-green-600 transition-colors">
                                                {selectedReferral?.r_info?.u_name || 'N/A'}
                                            </p>
                                            <p className="text-xs text-main-subfont mt-0.5">{selectedReferral?.r_info?.u_email || selectedReferral?.r_info?.u_device || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <FaExternalLinkAlt className="text-green-600 group-hover:translate-x-1 transition-transform" size={12} />
                                </div>
                            </div>

                            {/* Info Grid */}
                            <div className="bg-main-th border border-main-border rounded-xl p-4">
                                <p className="text-xs text-main-subfont font-medium uppercase tracking-wide mb-2">Created Date</p>
                                <p className="text-sm font-semibold text-main-font">{formatDate(selectedReferral?.createdAt)}</p>
                            </div>

                            {/* Referral Code */}
                            <div className="bg-main-th border border-main-blue/20 rounded-xl p-4">
                                <p className="text-xs text-main-blue font-medium uppercase tracking-wide mb-2">Referral Code</p>
                                <div className="bg-main-blue text-main-white px-4 py-2 rounded-lg text-center font-mono font-bold text-lg">
                                    {selectedReferral?.r_code}
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 bg-main-bg border-t border-main-border">
                            <button
                                onClick={() => { setShowDetailModal(false); setSelectedReferral(null); }}
                                className="w-full px-4 py-2.5 bg-main-blue text-main-white rounded-xl hover:bg-main-blue/90 transition-colors font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Referral;